import { Eye, Flag, Plus, RefreshCw, Trash2, Trophy } from 'lucide-react';
import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { Select } from '@components/select';
import { Spinner } from '@components/spinner';
import { Alert, AlertDescription } from '@components/ui/alert';
import { Badge } from '@components/ui/badge';
import { Button } from '@components/ui/button';
import { Card, CardContent } from '@components/ui/card';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '@components/ui/dialog';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from '@components/ui/table';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger
} from '@components/ui/tooltip';
import { toast } from '@core/toast';
import { UserContext } from '..';
import {
    useUserGoalsStore,
    UserGoalAssignmentDto,
    GoalAssignmentStatus,
    LinkedBadgeTypeSummary,
    UserGoalAssignmentDetailsDto,
    GoalProgressDto
} from './store';
import { AssignGoalModal } from '../badges/assign-goal-modal';

const ProgressBar = ({ value }: { value: number }) => (
    <span className="flex flex-col gap-1">
        <span className="text-xs tabular-nums">{value}%</span>
        <span className="h-1 w-full overflow-hidden rounded-full bg-muted">
            <span
                className={
                    value >= 100
                        ? 'block h-full bg-success'
                        : 'block h-full bg-primary'
                }
                style={{ width: `${Math.min(100, Math.max(0, value))}%` }}
            />
        </span>
    </span>
);

type HasBadgeFilter = 'all' | 'withBadge' | 'withoutBadge';

const hasBadgeOptions: { label: string; value: HasBadgeFilter }[] = [
    { label: 'All', value: 'all' },
    { label: 'With Badge', value: 'withBadge' },
    { label: 'Without Badge', value: 'withoutBadge' }
];

const getStatusVariant = (
    status: GoalAssignmentStatus
): 'success' | 'warning' | 'destructive' | 'secondary' => {
    switch (status) {
        case 'ACTIVE':
            return 'success';
        case 'COMPLETED':
            return 'success';
        case 'EXPIRED':
            return 'destructive';
        case 'PENDING':
            return 'warning';
        default:
            return 'secondary';
    }
};

const formatDate = (dateString?: string): string => {
    if (!dateString) return '-';
    try {
        return new Date(dateString).toLocaleDateString();
    } catch {
        return dateString;
    }
};

const formatRecurrence = (recurrence: string): string => {
    return recurrence
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, (c) => c.toUpperCase());
};

const getBadgeTypeName = (badgeType: LinkedBadgeTypeSummary): string => {
    return (
        badgeType.name?.en ||
        badgeType.name?.de ||
        Object.values(badgeType.name || {})[0] ||
        badgeType.key
    );
};

const BadgeTypeChip = ({
    badgeType
}: {
    badgeType: LinkedBadgeTypeSummary;
}) => (
    <Tooltip>
        <TooltipTrigger asChild>
            <Badge variant="outline">
                <Trophy />
                {getBadgeTypeName(badgeType)}
            </Badge>
        </TooltipTrigger>
        <TooltipContent>Badge Type: {badgeType.key}</TooltipContent>
    </Tooltip>
);

const IconAction = ({
    title,
    destructive,
    onClick,
    children
}: {
    title: string;
    destructive?: boolean;
    onClick: () => void;
    children: React.ReactNode;
}) => (
    <Tooltip>
        <TooltipTrigger asChild>
            <Button
                variant="ghost"
                size="icon-sm"
                onClick={onClick}
                className={destructive ? 'text-destructive' : undefined}
            >
                {children}
            </Button>
        </TooltipTrigger>
        <TooltipContent>{title}</TooltipContent>
    </Tooltip>
);

const EmptyGoals = ({ text }: { text: string }) => (
    <div className="flex flex-col items-center gap-2 py-8 text-center">
        <Flag className="size-12 text-muted-foreground/50" />
        <p className="text-sm text-muted-foreground">{text}</p>
    </div>
);

const CenteredSpinner = () => (
    <div className="flex justify-center py-8">
        <Spinner />
    </div>
);

export const UserGoals = observer(() => {
    const user = React.useContext(UserContext);
    const {
        userGoals,
        hasBadgeFilter,
        setHasBadgeFilter,
        loadUserGoals,
        userGoalDetails,
        userGoalProgresses,
        unassignGoal,
        refreshGoalProgressStreak,
        refreshGoalProgress
    } = useUserGoalsStore(user.id.toString());

    const [assignModalOpen, setAssignModalOpen] = useState(false);
    const [unassignDialog, setUnassignDialog] = useState<{
        open: boolean;
        goal: UserGoalAssignmentDto | null;
    }>({ open: false, goal: null });
    const [isUnassigning, setIsUnassigning] = useState(false);
    const [detailsDialog, setDetailsDialog] = useState<{
        open: boolean;
        assignmentId?: number;
    }>({ open: false });
    const [progressesDialog, setProgressesDialog] = useState<{
        open: boolean;
        goalKey?: string;
        assignmentId?: number;
    }>({ open: false });

    useEffect(() => {
        loadUserGoals();
    }, [loadUserGoals]);

    const handleRefresh = () => {
        loadUserGoals();
    };

    const handleUnassignClick = (goal: UserGoalAssignmentDto) => {
        setUnassignDialog({ open: true, goal });
    };

    const handleUnassignConfirm = async () => {
        if (!unassignDialog.goal?.assignmentId) return;

        setIsUnassigning(true);
        try {
            await unassignGoal(unassignDialog.goal.assignmentId);
            toast.success('Goal unassigned successfully');
            setUnassignDialog({ open: false, goal: null });
            loadUserGoals();
        } catch (error) {
            toast.error('Failed to unassign goal');
        } finally {
            setIsUnassigning(false);
        }
    };

    const handleUnassignCancel = () => {
        setUnassignDialog({ open: false, goal: null });
    };

    const handleAssignSuccess = () => {
        loadUserGoals();
    };

    const handleViewDetails = (goal: UserGoalAssignmentDto) => {
        if (!goal.assignmentId) {
            toast.warning('Goal assignment details are unavailable');
            return;
        }
        setDetailsDialog({ open: true, assignmentId: goal.assignmentId });
        userGoalDetails.request([goal.assignmentId]);
    };

    const handleDetailsClose = () => {
        setDetailsDialog({ open: false });
    };

    const handleViewProgresses = (goal: UserGoalAssignmentDto) => {
        if (!goal.assignmentId) {
            toast.warning('Goal assignment is unavailable');
            return;
        }
        setProgressesDialog({
            open: true,
            goalKey: goal.key,
            assignmentId: goal.assignmentId
        });
        userGoalProgresses.request([goal.assignmentId]);
    };

    const handleProgressesClose = () => {
        setProgressesDialog({ open: false });
    };

    const [refreshingProgress, setRefreshingProgress] = useState<{
        id: number;
        kind: 'streak' | 'progress';
    } | null>(null);

    const handleRefreshProgress = async (
        progress: GoalProgressDto,
        kind: 'streak' | 'progress'
    ) => {
        if (!progress.id) {
            toast.warning('Progress id is unavailable');
            return;
        }
        const label = kind === 'streak' ? 'streak' : 'progress';
        setRefreshingProgress({ id: progress.id, kind });
        try {
            if (kind === 'streak') {
                await refreshGoalProgressStreak(progress.id);
            } else {
                await refreshGoalProgress(progress.id);
            }
            toast.success(`Refreshed ${label}`);
            if (progressesDialog.assignmentId) {
                userGoalProgresses.request([progressesDialog.assignmentId]);
            }
        } catch (error) {
            toast.error(`Failed to refresh ${label}`);
        } finally {
            setRefreshingProgress(null);
        }
    };

    const formatDateTime = (value?: string): string => {
        if (!value) return '-';
        try {
            return new Date(value).toLocaleString();
        } catch {
            return value;
        }
    };

    const formatReward = (progress: GoalProgressDto): string => {
        const { reward } = progress;
        if (!reward) return '-';
        const parts: string[] = [];
        if (reward.value !== undefined) {
            parts.push(
                `${reward.value}${reward.currencyCode ? ' ' + reward.currencyCode : ''}`
            );
        }
        if (reward.badge) {
            parts.push(`Badge L${reward.badge.badgeLevelId}`);
        }
        return parts.length ? parts.join(' • ') : '-';
    };

    const renderDetailRow = (label: string, value?: React.ReactNode) => (
        <div className="flex justify-between gap-4 py-1">
            <span className="text-sm text-muted-foreground">{label}</span>
            <span className="text-right text-sm">{value ?? '-'}</span>
        </div>
    );

    const renderDetails = (details: UserGoalAssignmentDetailsDto) => {
        const progressPercent =
            details.currentProgress !== undefined && details.goalTargetValue
                ? Math.min(
                      Math.round(
                          (Number(details.currentProgress) /
                              Number(details.goalTargetValue)) *
                              100
                      ),
                      100
                  )
                : 0;

        const linkedBadges = (details.linkedBadgeTypes || []).map((bt) => ({
            id: bt.badgeTypeId,
            key: bt.badgeTypeKey,
            name: bt.badgeTypeName
        }));

        return (
            <div className="divide-y divide-border">
                {renderDetailRow('Goal Key', details.goalKey)}
                {renderDetailRow('Owner', details.goalOwner?.name)}
                {renderDetailRow(
                    'Recurrence',
                    formatRecurrence(details.goalRecurrence)
                )}
                {renderDetailRow('Day From', formatDate(details.dayFrom))}
                {renderDetailRow('Day To', formatDate(details.dayTo))}
                {renderDetailRow(
                    'Rewarded Day To',
                    formatDate(details.rewardedDayTo)
                )}
                {renderDetailRow(
                    'Progress',
                    details.currentProgress !== undefined
                        ? `${details.currentProgress} / ${details.goalTargetValue} (${progressPercent}%)`
                        : '-'
                )}
                {renderDetailRow(
                    'Status',
                    details.status ? (
                        <Badge variant={getStatusVariant(details.status)}>
                            {details.status}
                        </Badge>
                    ) : (
                        '-'
                    )
                )}
                {renderDetailRow(
                    'Linked Badges',
                    linkedBadges.length > 0 ? (
                        <span className="flex flex-wrap justify-end gap-1">
                            {linkedBadges.map((bt, idx) => (
                                <BadgeTypeChip
                                    key={`${bt.id}-${idx}`}
                                    badgeType={bt}
                                />
                            ))}
                        </span>
                    ) : (
                        'None'
                    )
                )}
            </div>
        );
    };

    const renderGoalsTable = (goals: UserGoalAssignmentDto[]) => (
        <Table>
            <TableHeader>
                <TableRow>
                    <TableHead>Goal Key</TableHead>
                    <TableHead>Owner</TableHead>
                    <TableHead>Recurrence</TableHead>
                    <TableHead>Day From</TableHead>
                    <TableHead>Day To</TableHead>
                    <TableHead className="text-center">Progress</TableHead>
                    <TableHead className="text-center">Status</TableHead>
                    <TableHead>Linked Badges</TableHead>
                    <TableHead className="text-center">Actions</TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                {goals.map((goal) => (
                    <TableRow key={goal.id}>
                        <TableCell className="font-mono text-[0.85rem]">
                            {goal.key}
                        </TableCell>
                        <TableCell>
                            {goal.owner?.name ? (
                                goal.owner.name
                            ) : (
                                <span className="text-muted-foreground">-</span>
                            )}
                        </TableCell>
                        <TableCell>
                            <Badge variant="outline">
                                {formatRecurrence(goal.recurrence)}
                            </Badge>
                        </TableCell>
                        <TableCell>
                            {formatDate(goal.assignmentDayFrom || goal.dayFrom)}
                        </TableCell>
                        <TableCell>
                            {formatDate(goal.assignmentDayTo || goal.dayTo)}
                        </TableCell>
                        <TableCell className="text-center">
                            {goal.progress !== undefined &&
                            goal.assignmentId ? (
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <Button
                                            variant="ghost"
                                            size="xs"
                                            className="h-auto w-full flex-col items-stretch gap-1 py-1"
                                            onClick={() =>
                                                handleViewProgresses(goal)
                                            }
                                        >
                                            <ProgressBar
                                                value={goal.progress}
                                            />
                                        </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        View goal progresses
                                    </TooltipContent>
                                </Tooltip>
                            ) : goal.progress !== undefined ? (
                                <ProgressBar value={goal.progress} />
                            ) : (
                                <span className="text-muted-foreground">-</span>
                            )}
                        </TableCell>
                        <TableCell className="text-center">
                            {goal.status && (
                                <Badge variant={getStatusVariant(goal.status)}>
                                    {goal.status}
                                </Badge>
                            )}
                        </TableCell>
                        <TableCell>
                            <div className="flex flex-wrap gap-1">
                                {goal.linkedBadgeTypes &&
                                goal.linkedBadgeTypes.length > 0 ? (
                                    goal.linkedBadgeTypes.map((bt, idx) => (
                                        <BadgeTypeChip
                                            key={`${bt.id}-${idx}`}
                                            badgeType={bt}
                                        />
                                    ))
                                ) : (
                                    <span className="text-sm text-muted-foreground">
                                        None
                                    </span>
                                )}
                            </div>
                        </TableCell>
                        <TableCell>
                            <div className="flex justify-center gap-1">
                                <IconAction
                                    title="View Details"
                                    onClick={() => handleViewDetails(goal)}
                                >
                                    <Eye />
                                </IconAction>
                                {goal.assignmentId && (
                                    <IconAction
                                        title="Unassign Goal"
                                        destructive
                                        onClick={() =>
                                            handleUnassignClick(goal)
                                        }
                                    >
                                        <Trash2 />
                                    </IconAction>
                                )}
                            </div>
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );

    const renderRefreshButton = (
        progress: GoalProgressDto,
        kind: 'streak' | 'progress'
    ) => (
        <Button
            variant="outline"
            size="xs"
            disabled={!progress.id || refreshingProgress?.id === progress.id}
            loading={
                refreshingProgress?.id === progress.id &&
                refreshingProgress?.kind === kind
            }
            onClick={() => handleRefreshProgress(progress, kind)}
        >
            {!(
                refreshingProgress?.id === progress.id &&
                refreshingProgress?.kind === kind
            ) && <RefreshCw />}
            {kind === 'streak' ? 'Streak' : 'Progress'}
        </Button>
    );

    const renderProgressesTable = (progresses: GoalProgressDto[]) => (
        <div className="overflow-x-auto">
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead>Day</TableHead>
                        <TableHead className="text-right">Value</TableHead>
                        <TableHead className="text-center">Level</TableHead>
                        <TableHead className="text-right">
                            Level Progress
                        </TableHead>
                        <TableHead className="text-center">Streak</TableHead>
                        <TableHead className="text-right">Points</TableHead>
                        <TableHead>Reward</TableHead>
                        <TableHead>Reward Date</TableHead>
                        <TableHead className="text-center">Actions</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {progresses.map((p, idx) => (
                        <TableRow key={`${p.day}-${idx}`}>
                            <TableCell>{formatDate(p.day)}</TableCell>
                            <TableCell className="text-right">
                                {p.value}
                            </TableCell>
                            <TableCell className="text-center">
                                {p.levelIndex}
                            </TableCell>
                            <TableCell className="text-right">
                                {p.currentLevelProgressAbsolute} (
                                {Math.round(
                                    (p.currentLevelProgressRelative || 0) * 100
                                )}
                                %)
                            </TableCell>
                            <TableCell className="text-center">
                                {p.streak}
                            </TableCell>
                            <TableCell className="text-right">
                                {p.points}
                            </TableCell>
                            <TableCell>{formatReward(p)}</TableCell>
                            <TableCell>
                                {p.reward ? formatDateTime(p.reward.date) : '-'}
                            </TableCell>
                            <TableCell>
                                <div className="flex justify-center gap-1">
                                    {renderRefreshButton(p, 'streak')}
                                    {renderRefreshButton(p, 'progress')}
                                </div>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div>
    );

    return (
        <div>
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <h2 className="text-lg font-semibold tracking-tight">
                    User Goals
                </h2>
                <div className="flex flex-wrap items-center gap-3">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setAssignModalOpen(true)}
                    >
                        <Plus />
                        Assign Badge Goals
                    </Button>
                    <div className="min-w-40">
                        <Select
                            label="Has Badge"
                            noLabel
                            value={hasBadgeFilter}
                            options={hasBadgeOptions}
                            onChange={(value: HasBadgeFilter) =>
                                setHasBadgeFilter(value)
                            }
                        />
                    </div>
                    <IconAction title="Refresh" onClick={handleRefresh}>
                        <RefreshCw />
                    </IconAction>
                </div>
            </div>

            {userGoals.match({
                default: () => null,
                loading: () => <CenteredSpinner />,
                error: () => (
                    <Alert variant="destructive" className="mb-4">
                        <AlertDescription>
                            Failed to load user goals. Please try again.
                        </AlertDescription>
                    </Alert>
                ),
                empty: () => <EmptyGoals text="No goals assigned yet" />,
                fetched: (goals: UserGoalAssignmentDto[]) =>
                    goals.length === 0 ? (
                        <EmptyGoals
                            text={
                                hasBadgeFilter
                                    ? 'No goals with badges found'
                                    : 'No goals assigned yet'
                            }
                        />
                    ) : (
                        <Card>
                            <CardContent className="overflow-x-auto p-0">
                                {renderGoalsTable(goals)}
                            </CardContent>
                        </Card>
                    )
            })}

            <Alert variant="info" className="mt-4">
                <AlertDescription>
                    This page shows goal assignments for the user. Goals can be
                    linked to badges - when a user completes a goal, they may
                    earn the associated badge.
                </AlertDescription>
            </Alert>

            <AssignGoalModal
                open={assignModalOpen}
                onClose={() => setAssignModalOpen(false)}
                userId={user.id.toString()}
                onSuccess={handleAssignSuccess}
            />

            <Dialog
                open={unassignDialog.open}
                onOpenChange={(open) => !open && handleUnassignCancel()}
            >
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Unassign Goal</DialogTitle>
                        <DialogDescription>
                            Are you sure you want to unassign the goal{' '}
                            <strong>{unassignDialog.goal?.key}</strong> from
                            this user?
                        </DialogDescription>
                    </DialogHeader>
                    <p className="text-sm text-muted-foreground">
                        This will remove the goal assignment and any associated
                        progress. This action cannot be undone.
                    </p>
                    <DialogFooter>
                        <Button
                            variant="ghost"
                            onClick={handleUnassignCancel}
                            disabled={isUnassigning}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={handleUnassignConfirm}
                            loading={isUnassigning}
                        >
                            Unassign
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog
                open={progressesDialog.open}
                onOpenChange={(open) => !open && handleProgressesClose()}
            >
                <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-4xl">
                    <DialogHeader>
                        <DialogTitle>Goal Progresses</DialogTitle>
                        {progressesDialog.goalKey && (
                            <DialogDescription className="font-mono">
                                {progressesDialog.goalKey}
                            </DialogDescription>
                        )}
                    </DialogHeader>
                    {userGoalProgresses.match({
                        default: () => null,
                        loading: () => <CenteredSpinner />,
                        error: () => (
                            <Alert variant="destructive">
                                <AlertDescription>
                                    Failed to load goal progresses. Please try
                                    again.
                                </AlertDescription>
                            </Alert>
                        ),
                        empty: () => (
                            <p className="text-sm text-muted-foreground">
                                No progresses found.
                            </p>
                        ),
                        fetched: (progresses: GoalProgressDto[]) =>
                            progresses.length === 0 ? (
                                <p className="text-sm text-muted-foreground">
                                    No progresses found.
                                </p>
                            ) : (
                                renderProgressesTable(progresses)
                            )
                    })}
                    <DialogFooter>
                        <Button variant="ghost" onClick={handleProgressesClose}>
                            Close
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog
                open={detailsDialog.open}
                onOpenChange={(open) => !open && handleDetailsClose()}
            >
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Goal Assignment Details</DialogTitle>
                    </DialogHeader>
                    {userGoalDetails.match({
                        default: () => null,
                        loading: () => <CenteredSpinner />,
                        error: () => (
                            <Alert variant="destructive">
                                <AlertDescription>
                                    Failed to load goal details. Please try
                                    again.
                                </AlertDescription>
                            </Alert>
                        ),
                        empty: () => (
                            <p className="text-sm text-muted-foreground">
                                No details found.
                            </p>
                        ),
                        fetched: (details: UserGoalAssignmentDetailsDto) =>
                            renderDetails(details)
                    })}
                    <DialogFooter>
                        <Button variant="ghost" onClick={handleDetailsClose}>
                            Close
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
});

export default UserGoals;
